import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  Form,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextAreaInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetAllEmailTemplate } from 'src/hooks/email-templates/get-all-platform-email-template.hook';

import { IApplicationTemplateFormProps } from './applications-form.interfaces';

import {
  FormWrapper,
  EmailTemplateFormWrapper,
  DeleteContainer,
  FormListWrapper,
} from './applications-form.styles';

const ApplicationsForm: FC<IApplicationTemplateFormProps> = ({ onClose, initialValues, onFinish, isEdit }) => {
  const { t } = useTranslation(['settings', 'common']);

  const { templates = [] } = useGetAllEmailTemplate();

  return (
    <EmailTemplateFormWrapper>
      <Form
        layout="vertical"
        onFinish={values => onFinish({ ...values })}
        initialValues={initialValues}
      >
        <FormWrapper>
          <div>
            <Form.Item
              name={'email'}
              label={t('settings:applications.labels.email')}
              rules={[
                { required: true, message: t('employee:validationError.emailRequired') },
                { type: 'email', message: t('common:validation.error.incorrectFormat') },
              ]}
            >
              <TextInput maxLength={50} placeholder="hr@example.com" />
            </Form.Item>
            <Form.Item
              name="eAUTemplateId"
              label={t('settings:applications.labels.eAUTemplate')}
              rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
            >
              <SelectInput>
                {templates?.map(({ _id, subject }: { _id: string; subject: string }) => (
                  <SelectInput.Option key={_id} value={_id}>
                    {subject}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
            <Form.Item
              name="eAUTemplateAcceptedId"
              label={t('settings:applications.labels.eAUTemplateAccepted')}
              rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
            >
              <SelectInput>
                {templates?.map(({ _id, subject }: { _id: string; subject: string }) => (
                  <SelectInput.Option key={_id} value={_id}>
                    {subject}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
            <Form.Item
              name="eAUTemplateRejectedId"
              label={t('settings:applications.labels.eAUTemplateRejected')}
              rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
            >
              <SelectInput>
                {templates?.map(({ _id, subject }: { _id: string; subject: string }) => (
                  <SelectInput.Option key={_id} value={_id}>
                    {subject}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
          </div>
          <Form.List name={'rejectReasons'} initialValue={isEdit ? undefined : [{}]}>
            {(fields, { add, remove }) => (
              <FormListWrapper isIcon={fields.length > 1}>
                {fields.map(field => (
                  <div key={field.key}>
                    <DeleteContainer isIcon={fields.length > 1}>
                      <Form.Item
                        name={[field.name, 'reason']}
                        rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
                        label={t('settings:applications.labels.rejectReason')}
                      >
                        <TextInput maxLength={50} placeholder={t('employee:labels.startTyping')} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Icon
                          icon={ICONS.trash}
                          rightMargin={false}
                          color={PLATFORM_THEME_COLORS.tone.red}
                          size={22}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </DeleteContainer>
                    <Form.Item
                      name={[field.name, 'rejectValue']}
                      rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
                      label={t('settings:applications.labels.rejectReasonValue')}
                    >
                      <TextAreaInput
                        showCount
                        maxLength={500}
                        placeholder={t(
                          'products:planList.createPlan.placeholders.writeDescription'
                        )}
                      />
                    </Form.Item>
                  </div>
                ))}
                <Button
                  type="text"
                  icon={ICONS.plus}
                  iconColor={PLATFORM_THEME_COLORS.primary.blue}
                  onClick={() => add()}
                >
                  {t('settings:applications.buttons.addRejectReason')}
                </Button>
              </FormListWrapper>
            )}
          </Form.List>
        </FormWrapper>

        <ButtonsWrapper marginTop={16}>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </EmailTemplateFormWrapper>
  );
};

export default ApplicationsForm;
